import { Box, Divider, ListItemSelect } from '@wix/design-system';
import React, { useContext } from 'react';

import { EditorContext, ModalServiceContext, RicosContext } from './../../../ricos-context';
import { useBiNext } from './../../../_shared/bi-context-provider/bi-context-provider';
import { Panel, PanelHeader, ThumbnailPicker } from './../../../ui-components';

import { GridPollIcon } from '../../assets/icons/GridPollIcon';
import { ListPollIcon } from '../../assets/icons/ListPollIcon';
import { WithImagePollIcon } from '../../assets/icons/WithImagePollIcon';
import { INSERT_POLL_MODAL_ID } from '../../consts';
import {
  INSERT_POLL_MODAL,
  INSERT_POLL_MODAL_THUMBNAIL,
  INSERT_POLL_MODAL_THUMBNAIL_PICKER,
} from '../../data-hooks';
import type { PollPluginEditorConfig } from '../../types';
import { POLL_TYPE } from '../../types';
import classes from './insert-modal-scss';
import type { PresetType } from './preset-data';
import { createInitialPollData, PRESET_TYPE } from './preset-data';

type Props = {
  config: PollPluginEditorConfig;
};

export const InsertModal: React.FC<Props> = ({ config }) => {
  const { t, isMobile } = useContext(RicosContext);
  const { getPublicCommands } = useContext(EditorContext);
  const modalService = useContext(ModalServiceContext);
  const logBi = useBiNext({ pluginId: 'poll', modalId: INSERT_POLL_MODAL_ID });

  const closeModal = () => modalService.closeModal(INSERT_POLL_MODAL_ID);

  const onPollAdd = (type: PresetType) => {
    const pollData = createInitialPollData(config, type);
    queueMicrotask(() => getPublicCommands().insertBlockWithBlankLines(POLL_TYPE, pollData));
    closeModal();
  };

  return (
    <Panel
      onCloseButtonClick={closeModal}
      width={isMobile ? '100%' : 430}
      height="100%"
      dataHook={INSERT_POLL_MODAL}
    >
      <PanelHeader
        title={t('Poll_Presets_Popup_Title')}
        subtitle={t('Poll_Presets_Popup_Subtitle')}
      />

      {isMobile ? (
        <Box direction="vertical">
          {presetOptions.map(item => (
            <>
              <ListItemSelect
                key={item.value}
                title={t(item.label)}
                dataHook={INSERT_POLL_MODAL_THUMBNAIL + item.value}
                onClick={() => {
                  logBi({ dataHook: INSERT_POLL_MODAL_THUMBNAIL_PICKER, value: item.value });
                  onPollAdd(item.value);
                }}
                size="medium"
                prefix={
                  <div
                    style={{
                      alignItems: 'center',
                      justifyContent: 'center',
                      display: 'flex',
                      border: '1px solid #d3d3d3',
                      marginBlock: '10px',
                    }}
                  >
                    <item.icon />
                  </div>
                }
              />
              <Divider />
            </>
          ))}
        </Box>
      ) : (
        <Box className={classes.thumbnailPicker} padding="SP3">
          <ThumbnailPicker
            selectedValue={undefined}
            onChange={value => {
              onPollAdd(value as PresetType);
            }}
            dataSource={presetOptions.map(option => ({
              value: option.value,
              dataHook: INSERT_POLL_MODAL_THUMBNAIL + option.value,
              renderBackgroundImage: () => <option.icon />,
              label: t(option.label),
            }))}
            size="medium"
            logBi={logBi}
            dataHook={INSERT_POLL_MODAL_THUMBNAIL_PICKER}
          />
        </Box>
      )}
    </Panel>
  );
};

const presetOptions = [
  {
    value: PRESET_TYPE.LIST,
    label: 'Poll_Presets_Popup_Option_TextList',
    icon: ListPollIcon,
  },
  {
    value: PRESET_TYPE.LIST_WITH_IMAGE,
    label: 'Poll_Presets_Popup_Option_TextListAndMainImage',
    icon: WithImagePollIcon,
  },
  {
    value: PRESET_TYPE.GRID,
    label: 'Poll_Presets_Popup_Option_ImagesGrid',
    icon: GridPollIcon,
  },
];
